/**@flow */

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { Button, SectionComponent, ConfirmationModal, View } from "@components";
import { AddEditEmployeePositionModal } from "@modules/employee/components";
import { RoleSummaryList } from "@modules/shared/components";
import actionCreators from "@modules/employee/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import type { State } from "@store/reducers";
import type { PropertiesSchema } from "@types";
import * as employeesSelectors from "@modules/employee/selectors";
import type { RoleSummary as RoleSummaryType } from "@modules/employee/reducer/employment-details/types";
import { getHierarchyTree } from "@modules/shared/selectors/hierarchy";
import type { HierarchyTree } from "@modules/shared/reducer/hierarchy/types";
import { transformToRequestData } from "./transformers";
import type { EmployeeType } from "@modules/employee/types";
import { getPermissions, getUserType } from "@modules/account/selectors";

type OwnProps = $Exact<{
  clientId: string | number,
  employeeId: string | number,
  schema: ?PropertiesSchema,
  roleSummary: Array<RoleSummaryType>,
  hasAdminPermissions: boolean,
  handlePageChange: Function,
}>;

type PropsFromState = $Exact<{
  isAddEmployeePositionModalVisible: boolean,
  tree: ?HierarchyTree,
  employee: ?EmployeeType,
  currentUserPermissions: { [permission: string]: boolean } | null,
  currentUserType: string,
}>;

type PropsFromDispatch = $Exact<{
  onAddEmployeePositionHandler: typeof actionCreators.ToggleAddEmployeePositionModal,
  onDismissAddEmployeePositionHandler: typeof actionCreators.ToggleAddEmployeePositionModal,
  onAddEmployeePosition: typeof actionCreators.AddEmployeePosition,
  deleteEmployeePosition: typeof actionCreators.DeleteEmployeePosition,
  getHierarchyTree: typeof sharedActionCreators.GetHierarchyTree,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export function RoleSummary(props: Props) {
  const defaultFormData = {
    fte: 1,
    // weeklyContractedHoursInSeconds defaults to UK 37.5hr working week e.g. 37.5 * 60 * 60
    weeklyContractedHoursInSeconds: 135000,
    // daysPerYear default working days per calendar year
    daysPerYear: 228,
  };

  const canDeletePosition = ["CLIENT_ADMIN", "ADMIN", "SERVICE_MANAGER"].includes(
    props.currentUserType,
  );
  const canRunDeleteQuery =
    props.currentUserPermissions && props.currentUserPermissions["privilege.employee.delete"];

  const [positionToDelete, setPositionToDelete] = useState(null);
  const [showConfirmDeletePositionModal, setShowConfirmDeletePositionModal] = useState(false);

  useEffect(() => {
    if (props.hasAdminPermissions) {
      props.getHierarchyTree({
        clientId: props.clientId,
        showEmployees: false,
      });
    }
  }, []);

  function addEmployeePosition(formData: Object) {
    const employeePositionData = transformToRequestData(formData);

    props.onAddEmployeePosition({
      formData: employeePositionData,
      clientId: props.clientId,
      employeeId: props.employeeId,
    });
  }

  const schema = props.schema &&
    props.hasAdminPermissions && {
      ...props.schema,
      properties: {
        ...(props.schema?.properties || {}),
        organisationalUnit: {
          ...(props.schema?.properties?.organisationalUnit?.properties?.id || {}),
          tree: props.tree,
        },
        shiftPattern: props.schema?.properties?.shiftPattern?.properties?.id || {},
        staffGroup: props.schema?.properties?.staffGroup?.properties?.id || {},
      },
    };

  function toggleConfirmDeletePositionModal(position) {
    setPositionToDelete(showConfirmDeletePositionModal ? null : position);
    setShowConfirmDeletePositionModal(!showConfirmDeletePositionModal);
  }

  function handleDeleteEmployeePosition() {
    toggleConfirmDeletePositionModal();
    canRunDeleteQuery &&
      props.deleteEmployeePosition({
        clientId: props.clientId,
        employeeId: props.employeeId,
        positionId: positionToDelete.id,
      });
  }

  return (
    <View className="gs-container">
      <SectionComponent
        title={<h6>{props.t("POSITION_HISTORY")}</h6>}
        rightLink={
          props.hasAdminPermissions && (
            <Button materialIcon="add_box" onClick={props.onAddEmployeePositionHandler}>
              {props.t("ADD")}
            </Button>
          )
        }
      >
        <RoleSummaryList
          roleSummary={props.roleSummary}
          clientId={props.clientId}
          employeeId={props.employeeId}
          schema={schema}
          handlePageChange={props.handlePageChange}
          handleDeleteEmployeePosition={toggleConfirmDeletePositionModal}
          canDeletePosition={canDeletePosition}
          canRunDeleteQuery={canRunDeleteQuery}
        />
        {props.schema && props.isAddEmployeePositionModalVisible && (
          <AddEditEmployeePositionModal
            onDismissHandler={props.onDismissAddEmployeePositionHandler}
            onSubmit={addEmployeePosition}
            schema={schema}
            tree={props.tree}
            formData={defaultFormData}
            title={props.t("ADD_ROLE")}
            submitButtonText={props.t("ADD")}
            submitButtonIcon="add_box"
            employee={props.employee}
          />
        )}
        {showConfirmDeletePositionModal && (
          <ConfirmationModal
            headerText={props.t("ARE_YOU_SURE")}
            bodyContent={props.t("YOU_SURE_WANT_DELETE_POSITION_DETAILED_DESCRIPTION")}
            cancelBtnLabel={props.t("NO_KEEP_POSITION")}
            cancelBtnColor="primary"
            confirmBtnLabel={props.t("YES_DELETE_POSITION")}
            confirmBtnIcon="replay_circle_filled"
            confirmBtnColor="negative"
            modalSize="normal"
            onCancel={toggleConfirmDeletePositionModal}
            onConfirm={handleDeleteEmployeePosition}
          />
        )}
      </SectionComponent>
    </View>
  );
}

const mapStateToProps = (state: State, OwnProps: Object): PropsFromState => ({
  isAddEmployeePositionModalVisible: employeesSelectors.getIsAddEmployeePositionModalVisible(state),
  employee: employeesSelectors.getEmployee(OwnProps.employeeId)(state),
  tree: getHierarchyTree(state),
  currentUserPermissions: getPermissions(state),
  currentUserType: getUserType(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  onAddEmployeePositionHandler: () => actionCreators.ToggleAddEmployeePositionModal(true),
  onDismissAddEmployeePositionHandler: () => actionCreators.ToggleAddEmployeePositionModal(false),
  onAddEmployeePosition: actionCreators.AddEmployeePosition,
  deleteEmployeePosition: actionCreators.DeleteEmployeePosition,
  getHierarchyTree: sharedActionCreators.GetHierarchyTree,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(RoleSummary));
